import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Facebook, Search, Share2, Target, Building2, 
  Users, MessageCircle, Bot, Globe, BarChart3, Hotel, 
  PieChart, Zap, MessageSquare, Cpu, Headphones, Award,
  Phone, Mail, MapPin, ChevronRight, Star, ArrowRight,
  CheckCircle2, Sparkles, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  heroData,
  servicesData,
  portfolioData,
  founderData,
  visionMissionData,
  whyChooseUsData,
  contactData
} from '../data/mock';

const iconMap = {
  TrendingUp, Facebook, Search, Share2, Target, Building2,
  Users, MessageCircle, Bot, Globe, BarChart3, Hotel,
  PieChart, Zap, MessageSquare, Cpu, Headphones, Award, Camera
};

const Home = () => {
  const { toast } = useToast();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Hero image carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroData.images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Success!",
          description: result.message,
        });
        setFormData({ name: '', email: '', phone: '', propertyType: '', message: '' });
      } else {
        throw new Error(result.detail || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_digitometrix-hotel/artifacts/5fq9ubw5_Screenshot%202026-03-23%20at%207.18.45%E2%80%AFPM.png" 
                alt="Digitometrix Technologies"
                className="logo-image"
              />
            </div>
            <nav className="nav-menu">
              <a href="#services">Services</a>
              <a href="#portfolio">Portfolio</a>
              <a href="#about">About</a>
              <a href="#contact">Contact</a>
              <a href="tel:+919867809539" className="header-phone">
                <Phone size={18} className="phone-icon" />
                <span>9867809539</span>
              </a>
              <Button onClick={scrollToContact} className="cta-button">
                Get Started
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          {heroData.images.map((img, idx) => (
            <div
              key={idx}
              className={`hero-image ${idx === currentImageIndex ? 'active' : ''}`}
              style={{ backgroundImage: `url(${img})` }}
            />
          ))}
          <div className="hero-overlay" />
        </div>
        
        <div className="container hero-content">
          <div className="hero-text">
            <h1 className="hero-headline">{heroData.headline}</h1>
            <p className="hero-subheadline">{heroData.subheadline}</p>
            <div className="hero-cta">
              <Button onClick={scrollToContact} size="lg" className="primary-cta">
                Book Free Strategy Call
                <ChevronRight className="ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="secondary-cta"
                onClick={() => window.open(`https://wa.me/${contactData.phone.replace(/\D/g, '')}`, '_blank')}
              >
                <MessageCircle className="mr-2" />
                WhatsApp Us
              </Button>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">8+</div>
                <div className="stat-label">Years Experience</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">500+</div>
                <div className="stat-label">Happy Clients</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">4.2x</div>
                <div className="stat-label">Average ROI</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Services</h2>
            <p className="section-subtitle">
              Comprehensive Performance Marketing Solutions for Hospitality Brands
            </p>
          </div>
          
          <div className="services-grid">
            {servicesData.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="service-card">
                  <CardContent className="service-content">
                    <div className="service-icon">
                      <Icon size={32} />
                    </div>
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-description">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="portfolio-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Hospitality Workfolio</h2>
            <p className="section-subtitle">
              Proven Success Stories from Our Hospitality Partners
            </p>
          </div>
          
          <div className="portfolio-grid">
            {portfolioData.map((project) => (
              <Card key={project.id} className="portfolio-card">
                <div className="portfolio-image-wrapper">
                  <img 
                    src={project.image} 
                    alt={project.name}
                    className="portfolio-image"
                  />
                  <div className="portfolio-overlay">
                    <div className="portfolio-results">
                      <div className="result-badge">
                        <TrendingUp size={16} />
                        <span>{project.results.bookings} Bookings</span>
                      </div>
                      <div className="result-badge">
                        <Users size={16} />
                        <span>{project.results.leads} Leads</span>
                      </div>
                      <div className="result-badge">
                        <Target size={16} />
                        <span>{project.results.roi} ROI</span>
                      </div>
                    </div>
                  </div>
                </div>
                <CardContent className="portfolio-content">
                  <h3 className="portfolio-name">{project.name}</h3>
                  <p className="portfolio-location">
                    <MapPin size={16} />
                    {project.location}
                  </p>
                  <p className="portfolio-services">{project.services}</p>
                  <div className="portfolio-testimonial">
                    <Star className="star-icon" />
                    <p className="testimonial-text">"{project.testimonial}"</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section id="about" className="founder-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Meet The Strategist Behind The Growth</h2>
          </div>
          
          <div className="founder-content">
            <div className="founder-image-wrapper">
              <img 
                src={founderData.image} 
                alt={founderData.name}
                className="founder-image"
              />
              <div className="founder-badge">
                <Award className="badge-icon" />
                <span>8+ Years</span>
              </div>
            </div>
            
            <div className="founder-details">
              <h3 className="founder-name">{founderData.name}</h3>
              <p className="founder-alias">({founderData.alias})</p>
              <p className="founder-title">{founderData.title}</p>
              <p className="founder-company">{founderData.company}</p>
              
              <p className="founder-bio">{founderData.bio}</p>
              
              <div className="founder-experience">
                {founderData.experience.map((item, idx) => (
                  <div key={idx} className="experience-item">
                    <CheckCircle2 size={20} className="check-icon" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="vision-mission-section">
        <div className="container">
          <div className="vision-mission-grid">
            <Card className="vision-card">
              <CardContent className="vision-content">
                <div className="vision-icon">
                  <Target size={40} />
                </div>
                <h3 className="vision-title">{visionMissionData.vision.title}</h3>
                <p className="vision-text">{visionMissionData.vision.description}</p>
              </CardContent>
            </Card>
            
            <Card className="mission-card">
              <CardContent className="mission-content">
                <div className="mission-icon">
                  <Sparkles size={40} />
                </div>
                <h3 className="mission-title">{visionMissionData.mission.title}</h3>
                <p className="mission-text">{visionMissionData.mission.description}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Digitometrix?</h2>
            <p className="section-subtitle">
              We Don't Just Generate Enquiries. We Generate ROI.
            </p>
          </div>
          
          <div className="why-choose-grid">
            {whyChooseUsData.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={item.id} className="why-choose-card">
                  <div className="why-icon">
                    <Icon size={28} />
                  </div>
                  <h4 className="why-title">{item.title}</h4>
                  <p className="why-description">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Let's Fill Your Rooms & Increase Your Revenue</h2>
            <p className="section-subtitle">
              Get in touch for a free consultation and discover how we can transform your bookings
            </p>
          </div>
          
          <div className="contact-grid">
            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">
                  <Phone size={24} />
                </div>
                <div className="contact-details">
                  <h4>Phone</h4>
                  <a href={`tel:${contactData.phone}`}>{contactData.phone}</a>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="contact-icon">
                  <Mail size={24} />
                </div>
                <div className="contact-details">
                  <h4>Email</h4>
                  <a href={`mailto:${contactData.email}`}>{contactData.email}</a>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="contact-icon">
                  <MapPin size={24} />
                </div>
                <div className="contact-details">
                  <h4>Address</h4>
                  <p>{contactData.address}</p>
                </div>
              </div>

              <div className="contact-cta-box">
                <h3>Ready to Scale Your Bookings?</h3>
                <p>Book a free strategy call and let's discuss your growth goals</p>
                <Button 
                  size="lg" 
                  className="whatsapp-cta"
                  onClick={() => window.open(`https://wa.me/${contactData.phone.replace(/\D/g, '')}`, '_blank')}
                >
                  <MessageCircle className="mr-2" />
                  WhatsApp Now
                </Button>
              </div>
            </div>
            
            <Card className="contact-form-card">
              <CardContent className="contact-form-content">
                <h3 className="form-title">Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <Input
                      type="email"
                      name="email"
                      placeholder="Your Email *"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Your Phone *"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <Input
                      type="text"
                      name="propertyType"
                      placeholder="Property Type (Hotel/Resort/Villa)"
                      value={formData.propertyType}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <Textarea
                      name="message"
                      placeholder="Tell us about your property and goals *"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="form-textarea"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Book Free Consultation'}
                    <ArrowRight className="ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <img 
                  src="https://customer-assets.emergentagent.com/job_digitometrix-hotel/artifacts/5fq9ubw5_Screenshot%202026-03-23%20at%207.18.45%E2%80%AFPM.png" 
                  alt="Digitometrix Technologies"
                  className="footer-logo-image"
                />
              </div>
              <p className="footer-tagline">
                Turning Hospitality Properties Into Booking Machines
              </p>
              <p className="footer-year">© 2017-2025 Digitometrix Technologies. All rights reserved.</p>
            </div>
            
            <div className="footer-section">
              <h4 className="footer-heading">Quick Links</h4>
              <ul className="footer-links">
                <li><a href="#services">Services</a></li>
                <li><a href="#portfolio">Portfolio</a></li>
                <li><a href="#about">About Us</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li><a href="#services">Performance Marketing</a></li>
                <li><a href="#services">Meta Ads Management</a></li>
                <li><a href="#services">Google Ads Management</a></li>
                <li><a href="#services">Lead Generation</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4 className="footer-heading">Contact</h4>
              <ul className="footer-contact">
                <li>
                  <Phone size={16} />
                  <a href={`tel:${contactData.phone}`}>{contactData.phone}</a>
                </li>
                <li>
                  <Mail size={16} />
                  <a href={`mailto:${contactData.email}`}>{contactData.email}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href={`https://wa.me/${contactData.phone.replace(/\D/g, '')}`}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Contact us on WhatsApp"
      >
        <MessageCircle size={28} />
      </a>
    </div>
  );
};

export default Home;
